import {Fragment, useEffect, useState, type ReactNode} from 'react';
import {getLocale, pageMessage, registerPageMessages, useT, type LocaleCode} from './i18n';

/** App-owned message catalogs, loaded once per language. No DOM replacement or translation service. */
const loads = new Map<LocaleCode, Promise<void>>();
const catalogUrls = import.meta.glob<string>('../locales/pages/*.json', {query: '?url', import: 'default', eager: true});
export function loadPageMessages(code: LocaleCode): Promise<void> {
  if (code === 'en') return Promise.resolve();
  let pending = loads.get(code);
  if (!pending) {
    pending = fetch(catalogUrls[`../locales/pages/${code}.json`], {credentials: 'omit'}).then(async response => {
      if (!response.ok) throw new Error('Language catalog unavailable');
      const messages: unknown = await response.json();
      if (!messages || typeof messages !== 'object' || Array.isArray(messages) || !Object.values(messages).every(value => typeof value === 'string')) throw new Error('Invalid language catalog');
      registerPageMessages(code, messages as Record<string, string>);
    }).catch(error => { loads.delete(code); throw error; });
    loads.set(code, pending);
  }
  return pending;
}

/** Called once at the top of each component that renders page messages. */
export function usePageText() { return useT(); }

/** Load the initial page language before showing forms; subsequent changes preserve the mounted tree. */
export function InitialPageLanguage({children}: {children: ReactNode}) {
  const {t, locale} = useT();
  const [ready, setReady] = useState(locale === 'en'), [failed, setFailed] = useState(false), [retry, setRetry] = useState(0);
  useEffect(() => {
    if (ready) return;
    let current = true;
    setFailed(false);
    void loadPageMessages(locale).then(() => {if (current) setReady(true);}).catch(() => {if (current) setFailed(true);});
    return () => {current = false;};
  }, [locale, ready, retry]);
  if (ready) return children;
  return <div className="card" style={{margin: 24}} role={failed ? 'alert' : 'status'}>{t(failed ? 'dashboard.loadFailed' : 'common.loading')}{failed && <button className="btn outline" onClick={() => setRetry(n => n + 1)}>{t('common.retry')}</button>}</div>;
}

function valueText(value: string | number | null | undefined): string {
  return typeof value === 'number' ? new Intl.NumberFormat(getLocale()).format(value) : value == null ? '' : value;
}
export function tx(source: string, values?: Record<string, string | number | null | undefined>): string {
  return pageMessage(source).replace(/\{(\w+)\}/g, (literal, key) => values && Object.hasOwn(values, key) ? valueText(values[key]) : literal);
}

/** Interpolation can reorder text and inline elements without translating user content or losing handlers. */
export function ptx(source: string, values: Record<string, ReactNode>): ReactNode {
  return pageMessage(source).split(/(\{\w+\})/g).map((part, index) => {
    const key = /^\{(\w+)\}$/.exec(part)?.[1];
    const value = key && Object.hasOwn(values, key) ? values[key] : part;
    return <Fragment key={key ?? index}>{typeof value === 'number' ? valueText(value) : value}</Fragment>;
  });
}

export function pageError(error: unknown, fallback: string): string {
  const data = (error as {response?: {data?: {message?: unknown}}})?.response?.data?.message;
  const messages = Array.isArray(data) ? data.filter((item): item is string => typeof item === 'string') : typeof data === 'string' ? [data] : [];
  return messages.length ? messages.map(message => tx(message)).join(' · ') : tx(fallback);
}
